// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

import * as MaintenanceNoteActions from '../actions/maintenance-note.actions';
import { MaintenanceNoteApiService, NotificationService } from '@fc-core';
import { MatDialog } from '@angular/material/dialog';

@Injectable()
export class MaintenanceNoteEffects {
  constructor(
    private actions$: Actions,
    private maintenanceNoteApi: MaintenanceNoteApiService,
    private dialog: MatDialog,
    private notifications: NotificationService,
  ) {}

  loadMaintenanceNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceNoteActions.loadMaintenanceNotes),
      switchMap(({ vehicle }) => {
        return this.maintenanceNoteApi.loadAll(vehicle).pipe(
          map((maintenanceNotes) => {
            return MaintenanceNoteActions.loadMaintenanceNotesSuccess({
              notes: maintenanceNotes,
            });
          }),
          catchError((err) =>
            of(MaintenanceNoteActions.maintenanceNotesError({ err })),
          ),
        );
      }),
    ),
  );

  addMaintenanceNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceNoteActions.addMaintenanceNote),
      switchMap(({ note }) => {
        return this.maintenanceNoteApi.create(note).pipe(
          mergeMap((maintenanceNote) => {
            if ((note as any)['filesList']?.length) {
              return this.maintenanceNoteApi
                .addMaintenanceNoteAttachments(
                  (note as any)['filesList'],
                  maintenanceNote.id,
                )
                .pipe(
                  map((files) => {
                    this.notifications.showSuccess(
                      'Maintenance successfully added',
                    );
                    this.dialog.closeAll();
                    return MaintenanceNoteActions.addMaintenanceNoteSuccess({
                      note: { ...maintenanceNote, files: files },
                    });
                  }),
                );
            }
            this.dialog.closeAll();
            return [
              MaintenanceNoteActions.addMaintenanceNoteSuccess({
                note: maintenanceNote,
              }),
            ];
          }),
          catchError((err) =>
            of(MaintenanceNoteActions.maintenanceNotesError({ err })),
          ),
        );
      }),
    ),
  );

  updateMaintenanceNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceNoteActions.updateMaintenanceNote),
      switchMap(({ note }) => {
        return this.maintenanceNoteApi.update(note).pipe(
          mergeMap((maintenanceNote) => {
            if ((note as any)['filesList']?.length) {
              return this.maintenanceNoteApi
                .updateMaintenanceNoteAttachments(
                  (note as any)['filesList'],
                  maintenanceNote.id,
                )
                .pipe(
                  map((files) => {
                    this.notifications.showSuccess(
                      'Maintenance successfully updated',
                    );
                    this.dialog.closeAll();
                    return MaintenanceNoteActions.updateMaintenanceNoteSuccess({
                      note: { ...maintenanceNote, files: files },
                    });
                  }),
                );
            } else {
              this.dialog.closeAll();
              return [
                MaintenanceNoteActions.updateMaintenanceNoteSuccess({
                  note: maintenanceNote,
                }),
              ];
            }
          }),
          catchError((err) =>
            of(MaintenanceNoteActions.maintenanceNotesError({ err })),
          ),
        );
      }),
    ),
  );

  deleteMaintenanceNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceNoteActions.deleteMaintenanceNote),
      switchMap(({ note }) => {
        return this.maintenanceNoteApi.delete(note).pipe(
          map((maintenanceNote) => {
            this.notifications.showSuccess('Maintenance successfully deleted');
            this.dialog.closeAll();
            return MaintenanceNoteActions.deleteMaintenanceNoteSuccess({
              note: maintenanceNote,
            });
          }),
          catchError((err) =>
            of(MaintenanceNoteActions.maintenanceNotesError({ err })),
          ),
        );
      }),
    ),
  );
  deleteMaintenanceNoteAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceNoteActions.deleteMaintenanceNoteAttachment),
      switchMap(({ note, file }) => {
        return this.maintenanceNoteApi.deleteAttachment(note, file).pipe(
          map(() => {
            this.notifications.showSuccess(
              'Maintenance note attachment successfully deleted',
            );
            this.dialog.closeAll();
            return MaintenanceNoteActions.deleteMaintenanceNoteAttachmentSuccess(
              {
                note,
                file,
              },
            );
          }),
          catchError((err) =>
            of(MaintenanceNoteActions.maintenanceNotesError({ err })),
          ),
        );
      }),
    ),
  );
  deleteMaintenanceNoteAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceNoteActions.deleteMaintenanceNoteAttachments),
      switchMap(({ note, files }) => {
        return this.maintenanceNoteApi.deleteManyAttachments(note, files).pipe(
          map(() => {
            this.notifications.showSuccess(
              'Maintenance note attachments successfully deleted',
            );
            this.dialog.closeAll();
            return MaintenanceNoteActions.deleteMaintenanceNoteAttachmentsSuccess(
              {
                note,
                files,
              },
            );
          }),
          catchError((err) =>
            of(MaintenanceNoteActions.maintenanceNotesError({ err })),
          ),
        );
      }),
    ),
  );
}
